import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
	AppBar,
	Toolbar,
	Typography,
	Drawer,
	SwipeableDrawer,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Box,
	Divider,
	IconButton,
	Menu,
	MenuItem,
	Avatar,
	Chip,
	ListSubheader,
	useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PaymentIcon from '@mui/icons-material/Payment';
import StorageIcon from '@mui/icons-material/Storage';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailIcon from '@mui/icons-material/Email';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import UploadIcon from '@mui/icons-material/Upload';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useAuth } from '../context/AuthContext';
import { AssistantProvider, AssistantWidget } from '../components/assistant/AssistantProvider';

const drawerWidth = 240;

const navSections = [
	{
		label: 'Operativita',
		items: [
			{ label: 'Dashboard', to: '/crm', icon: <DashboardIcon /> },
			{ label: 'Clienti', to: '/crm/clienti', icon: <PeopleIcon /> },
			{ label: 'Contratti', to: '/crm/contratti', icon: <AssignmentIcon /> },
			{ label: 'Impianti', to: '/crm/impianti', icon: <LocalGasStationIcon /> },
			{ label: 'Contatori', to: '/crm/contatori', icon: <SpeedIcon /> },
			{ label: 'Letture', to: '/crm/letture', icon: <ReceiptLongIcon /> },
			{ label: 'Letturisti', to: '/crm/letturisti', icon: <PersonPinIcon /> },
			{ label: 'Serbatoi', to: '/crm/serbatoi', icon: <StorageIcon /> },
			{ label: 'Rifornimenti', to: '/crm/rifornimenti', icon: <LocalShippingIcon /> }
		]
	},
	{
		label: 'Commerciale',
		items: [
			{ label: 'Fatture', to: '/crm/fatture', icon: <ReceiptLongIcon /> },
			{ label: 'Solleciti', to: '/crm/solleciti', icon: <NotificationsActiveIcon /> },
			{ label: 'Pagamenti', to: '/crm/pagamenti', icon: <PaymentIcon /> },
			{ label: 'Report', to: '/crm/report', icon: <SummarizeIcon /> }
		]
	},
	{
		label: 'Messaggi e AI',
		items: [
			{ label: 'Email', to: '/crm/email/inbox', icon: <EmailIcon /> },
			{ label: 'AI Assistant', to: '/crm/email/ai-dashboard', icon: <PsychologyIcon /> },
			{ label: 'WhatsApp', to: '/crm/whatsapp', icon: <WhatsAppIcon /> },
			{ label: 'WA Matching', to: '/crm/whatsapp/phone-matching', icon: <WhatsAppIcon /> },
			{ label: 'Comunicazioni', to: '/crm/comunicazioni', icon: <CampaignIcon /> },
			{ label: 'Assistente', to: '/crm/assistant', icon: <SmartToyIcon /> }
		]
	},
	{
		label: 'Sistema',
		items: [
			{ label: 'Importa Dati', to: '/crm/importa', icon: <UploadIcon /> },
			{ label: 'Impostazioni', to: '/crm/impostazioni', icon: <SettingsIcon /> }
		]
	}
];

const navItems = navSections.flatMap((section) => section.items);

// Get current page title from path
const getPageTitle = (pathname) => {
	const item = [...navItems].sort((a, b) => b.to.length - a.to.length).find(item => {
		if (item.to === '/crm') return pathname === '/crm';
		return pathname.startsWith(item.to);
	});
	return item ? item.label : 'GPL Service CRM';
};

// Layout principale con AppBar e Drawer laterale permanente (desktop) / temporaneo (mobile)
const MainLayout = () => {
	const { user, tenant, tenantName, logout } = useAuth();
	const [anchorEl, setAnchorEl] = useState(null);
	const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
	const menuOpen = Boolean(anchorEl);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const location = useLocation();
	const activeTenantName = tenantName || tenant?.name || 'Tenant non disponibile';

	useEffect(() => {
		if (isMobile) {
			setMobileDrawerOpen(false);
		}
	}, [isMobile, location.pathname]);

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = () => {
		handleMenuClose();
		logout();
	};

	const handleDrawerToggle = () => {
		setMobileDrawerOpen(!mobileDrawerOpen);
	};

	const drawerContent = (
		<>
			<Toolbar />
			<Box sx={{ px: 2, py: 2 }}>
				<Typography variant="subtitle1" fontWeight={700}>
					Gestionale GPL
				</Typography>
				<Typography variant="body2" color="text.secondary" noWrap>
					{activeTenantName}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} noWrap>
					{user?.nome || user?.username || 'Utente'}
				</Typography>
			</Box>
			<Divider />
			{navSections.map((section) => (
				<List
					key={section.label}
					subheader={
						<ListSubheader
							disableSticky
							sx={{
								bgcolor: 'transparent',
								lineHeight: '32px',
								fontSize: '0.75rem',
								fontWeight: 700,
								color: 'text.secondary',
								textTransform: 'uppercase'
							}}
						>
							{section.label}
						</ListSubheader>
					}
				>
					{section.items.map((item) => (
						<ListItemButton
							key={item.to}
							component={NavLink}
							to={item.to}
							onClick={isMobile ? () => setMobileDrawerOpen(false) : undefined}
							sx={{
								py: isMobile ? 1.2 : 1,
								'&.active': {
									bgcolor: 'action.selected',
									borderRight: '3px solid',
									borderColor: 'primary.main'
								}
							}}
						>
							<ListItemIcon>{item.icon}</ListItemIcon>
							<ListItemText primary={item.label} />
						</ListItemButton>
					))}
				</List>
			))}
		</>
	);

	return (
		<Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
			<AppBar 
				position="fixed" 
				sx={{ 
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
			>
				<Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
					{/* Hamburger menu button - mobile only */}
					{isMobile && (
						<IconButton
							color="inherit"
							aria-label="apri menu"
							edge="start"
							onClick={handleDrawerToggle}
							sx={{ mr: 1 }}
						>
							<MenuIcon />
						</IconButton>
					)}
					
					<Typography
						variant="h6"
						component="div"
						sx={{
							flexGrow: 1,
							fontSize: { xs: '1rem', sm: '1.25rem' },
							minWidth: 0,
							mr: 1,
							lineHeight: 1.2,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: isMobile ? 'normal' : 'nowrap',
							display: '-webkit-box',
							WebkitBoxOrient: 'vertical',
							WebkitLineClamp: isMobile ? 2 : 1
						}}
					>
						{isMobile ? getPageTitle(location.pathname) : 'Gestionale GPL'}
						{tenantName && !isMobile && (
							<Chip
								icon={<BusinessIcon />}
								label={tenantName}
								size="small"
								color="secondary"
								sx={{ ml: 2, color: 'white' }}
							/>
						)}
					</Typography>
					
					{/* Menu Utente */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
							{user?.nome || user?.username}
						</Typography>
						<IconButton
							onClick={handleMenuOpen}
							size="small"
							sx={{ color: 'white' }}
							aria-controls={menuOpen ? 'account-menu' : undefined}
							aria-haspopup="true"
							aria-expanded={menuOpen ? 'true' : undefined}
						>
							<Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
								{(user?.nome || user?.username || 'U').charAt(0).toUpperCase()}
							</Avatar>
						</IconButton>
					</Box>
					
					<Menu
						anchorEl={anchorEl}
						id="account-menu"
						open={menuOpen}
						onClose={handleMenuClose}
						onClick={handleMenuClose}
						PaperProps={{
							elevation: 3,
							sx: { minWidth: 200, mt: 1 }
						}}
						transformOrigin={{ horizontal: 'right', vertical: 'top' }}
						anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					>
						<MenuItem disabled>
							<ListItemIcon>
								<AccountCircleIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText 
								primary={user?.nome || user?.username}
								secondary={user?.email}
							/>
						</MenuItem>
						{tenant && (
							<MenuItem disabled>
								<ListItemIcon>
									<BusinessIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText 
									primary={tenant.name}
									secondary={`Tenant: ${tenant.slug}`}
								/>
							</MenuItem>
						)}
						<Divider />
						<MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
							<ListItemIcon>
								<LogoutIcon fontSize="small" color="error" />
							</ListItemIcon>
							<ListItemText primary="Esci" />
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>

			{/* Drawer - Permanent on desktop, Swipeable temporary on mobile */}
			{isMobile ? (
				<SwipeableDrawer
					variant="temporary"
					open={mobileDrawerOpen}
					onClose={() => setMobileDrawerOpen(false)}
					onOpen={() => setMobileDrawerOpen(true)}
					ModalProps={{ keepMounted: true }}
					sx={{
						'& .MuiDrawer-paper': {
							width: drawerWidth,
							boxSizing: 'border-box'
						}
					}}
				>
					{drawerContent}
				</SwipeableDrawer>
			) : (
				<Drawer
					variant="permanent"
					sx={{
						width: drawerWidth,
						flexShrink: 0,
						[`& .MuiDrawer-paper`]: {
							width: drawerWidth,
							boxSizing: 'border-box'
						}
					}}
				>
					{drawerContent}
				</Drawer>
			)}

			<Box 
				component="main" 
				sx={{ 
					flexGrow: 1, 
					p: { xs: 1, sm: 2, md: 3 },
					width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
					maxWidth: '100%',
					overflow: 'hidden',
				}}
			>
				<Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
				<Box sx={{ width: '100%' }}>
					<Outlet />
				</Box>
			</Box>
		</Box>
	);
};

export default function MainLayoutWithAssistant() {
  return <AssistantProvider><MainLayout /><AssistantWidget /></AssistantProvider>;
}
